import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useToast } from '../hooks/use-toast';

import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowsClockwise,
  CaretDown,
  CheckCircle,
  CircleNotch,
  Database,
  Funnel,
  Lightning,
  LinkSimple,
  MagnifyingGlass,
  Sparkle,
  UploadSimple,
  WarningCircle
} from '@phosphor-icons/react';
const AdminTemplateManager = () => {
  const { toast } = useToast();
  
  // Data state
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Funnel state
  const [filters, setFilters] = useState({
    marketplace_status: '',
    capability_mode: '',
    source_tag: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (filters.marketplace_status) params.append('marketplace_status', filters.marketplace_status);
      if (filters.capability_mode) params.append('capability_mode', filters.capability_mode);
      if (filters.source_tag) params.append('source_tag', filters.source_tag);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/templates/manager/list?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/templates/manager/stats`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/templates/manager/recommendations?target_count=76`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchStats();
    fetchRecommendations();
  }, [fetchTemplates, fetchStats, fetchRecommendations]);

  // Promote template
  const handlePromote = async (templateId, templateName) => {
    try {
      setActionLoading(templateId);
      const response = await fetch(`${BACKEND_URL}/api/admin/templates/manager/promote`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: 'Template Promoted',
          description: `"${templateName}" is now visible in the marketplace`,
        });
        fetchTemplates();
        fetchStats();
        fetchRecommendations();
      } else {
        toast({
          title: 'Promotion Failed',
          description: data.detail || data.message || 'Failed to promote template',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to promote template',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Archive template
  const handleArchive = async (templateId, templateName) => {
    try {
      setActionLoading(templateId);
      const response = await fetch(`${BACKEND_URL}/api/admin/templates/manager/archive`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: 'Template Archived',
          description: `"${templateName}" is now hidden from the marketplace`,
        });
        fetchTemplates();
        fetchStats();
        fetchRecommendations();
      } else {
        toast({
          title: 'Archive Failed',
          description: data.detail || data.message || 'Failed to archive template',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to archive template',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Capability badge
  const getCapabilityBadge = (mode) => {
    const badges = {
      'text_only': { label: 'Text Only', color: 'bg-gray-500/20 text-gray-300' },
      'automation_ready': { label: 'Automation Ready', color: 'bg-green-500/20 text-green-400' },
      'integration_dependent': { label: 'Needs Integration', color: 'bg-amber-500/20 text-amber-400' }
    };
    const badge = badges[mode] || badges['text_only'];
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  // Status badge
  const getStatusBadge = (status) => {
    if (status === 'published') {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">Published</span>;
    }
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400">Archived</span>;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Template Manager</h1>
          <p className="text-gray-400 text-sm">
            Admin-only page to manage marketplace template visibility
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center gap-3">
                <Database size={20} className="text-[#6366F1]" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-gray-400">Total Templates</p>
                </div>
              </div>
            </div>
            <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center gap-3">
                <CheckCircle size={20} className="text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.published}</p>
                  <p className="text-xs text-gray-400">Published</p>
                </div>
              </div>
            </div>
            <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center gap-3">
                <Archive size={20} className="text-gray-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.archived}</p>
                  <p className="text-xs text-gray-400">Archived</p>
                </div>
              </div>
            </div>
            <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center gap-3">
                <Sparkle size={20} className="text-amber-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{recommendations?.slots_available || 0}</p>
                  <p className="text-xs text-gray-400">Slots to 76</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations Panel */}
        {recommendations && recommendations.recommendations?.length > 0 && (
          <div className="glass rounded-xl p-4 border border-amber-500/30 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkle size={18} className="text-amber-400" />
                <h3 className="text-sm font-semibold text-white">
                  Recommended Templates to Promote ({recommendations.recommendations.length})
                </h3>
              </div>
              <span className="text-xs text-gray-400">
                Target: {recommendations.target} | Current: {recommendations.current_published}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {recommendations.recommendations.slice(0, 6).map((rec) => (
                <div key={rec.id} className="flex items-center justify-between p-2 bg-[rgba(255,255,255,0.03)] rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{rec.name}</p>
                    <p className="text-xs text-gray-500">{rec.category} • Score: {rec.score}</p>
                  </div>
                  <button
                    onClick={() => handlePromote(rec.id, rec.name)}
                    disabled={actionLoading === rec.id}
                    className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded hover:bg-green-500/30 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === rec.id ? <CircleNotch size={12} className="animate-spin" /> : <ArrowUp size={12} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)] mb-4">
          <div className="flex items-center gap-4">
            {/* MagnifyingGlass */}
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-9 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm focus:outline-none focus:border-[#6366F1]"
              />
            </div>
            
            {/* Status Funnel */}
            <select
              value={filters.marketplace_status}
              onChange={(e) => setFilters(f => ({ ...f, marketplace_status: e.target.value }))}
              className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm"
            >
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            
            {/* Capability Funnel */}
            <select
              value={filters.capability_mode}
              onChange={(e) => setFilters(f => ({ ...f, capability_mode: e.target.value }))}
              className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm"
            >
              <option value="">All Capabilities</option>
              <option value="text_only">Text Only</option>
              <option value="automation_ready">Automation Ready</option>
              <option value="integration_dependent">Needs Integration</option>
            </select>
            
            {/* Source Funnel */}
            <select
              value={filters.source_tag}
              onChange={(e) => setFilters(f => ({ ...f, source_tag: e.target.value }))}
              className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm"
            >
              <option value="">All Sources</option>
              <option value="canonical">Canonical</option>
              <option value="native_integration">Native Integration</option>
              <option value="legacy">Legacy</option>
            </select>
            
            {/* ArrowsClockwise */}
            <button
              onClick={() => { fetchTemplates(); fetchStats(); fetchRecommendations(); }}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowsClockwise size={18} />
            </button>
          </div>
        </div>

        {/* Templates Table */}
        <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.1)]">
                <th className="text-left p-3 text-xs font-medium text-gray-400">Name</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Category</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Capability</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Integrations</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Source</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Status</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <CircleNotch className="w-6 h-6 animate-spin text-[#6366F1] mx-auto" />
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-400">
                    No templates found
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id} className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.02)]">
                    <td className="p-3">
                      <p className="text-sm text-white font-medium truncate max-w-[200px]">{template.name}</p>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-gray-400">{template.category || 'Uncategorized'}</span>
                    </td>
                    <td className="p-3">
                      {getCapabilityBadge(template.capability_mode)}
                    </td>
                    <td className="p-3">
                      {template.integration_requirements?.required?.length > 0 ? (
                        <div className="flex items-center gap-1">
                          <LinkSimple size={12} className="text-amber-400" />
                          <span className="text-xs text-gray-400">
                            {template.integration_requirements.required.join(', ')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">None</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-gray-500 capitalize">{template.source_tag || 'unknown'}</span>
                    </td>
                    <td className="p-3">
                      {getStatusBadge(template.marketplace_status)}
                    </td>
                    <td className="p-3 text-right">
                      {template.marketplace_status === 'archived' ? (
                        <button
                          onClick={() => handlePromote(template.id, template.name)}
                          disabled={actionLoading === template.id}
                          className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded hover:bg-green-500/30 transition-colors disabled:opacity-50 flex items-center gap-1 ml-auto"
                        >
                          {actionLoading === template.id ? (
                            <CircleNotch size={12} className="animate-spin" />
                          ) : (
                            <>
                              <ArrowUp size={12} />
                              Promote
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchive(template.id, template.name)}
                          disabled={actionLoading === template.id}
                          className="px-3 py-1 bg-gray-500/20 text-gray-400 text-xs rounded hover:bg-gray-500/30 transition-colors disabled:opacity-50 flex items-center gap-1 ml-auto"
                        >
                          {actionLoading === template.id ? (
                            <CircleNotch size={12} className="animate-spin" />
                          ) : (
                            <>
                              <Archive size={12} />
                              Archive
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-3 border-t border-[rgba(255,255,255,0.1)]">
            <p className="text-xs text-gray-400">
              Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * limit >= total}
                className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTemplateManager;
